import { ConnectProto } from "@racoon/api";
import { plannedContainerCount } from "./job-containers";
import { isFinalJobStatus } from "./job-status";

/** Nominal volume: container type volume × container count, 0 without data. */
export const getJobVolume = (job: ConnectProto.Job): number => {
  const volume = Number(job.containerGroup?.containerType?.volume ?? 0);
  const containers = plannedContainerCount(job);
  return volume > 0 && containers > 0 ? volume * containers : 0;
};

// Zero is a real consignment value; only a missing volume renders as "-".
export const formatVolume = (volume: number | undefined) =>
  volume == null || Number.isNaN(volume) ? "-" : String(Math.round(volume * 100) / 100);

/** A job that ended without its containers being taken: cancelled, or not carried out (невывоз). */
const tookNothing = (job: ConnectProto.Job) =>
  job.status === ConnectProto.JobStatus.CANCELLED ||
  job.status === ConnectProto.JobStatus.NOT_COMPLETED;

/**
 * m³ actually taken at a job. A finished job always carries its container fills;
 * none recorded means the containers were exactly as full as planned, so the
 * nominal volume stands. An unfinished job has no fact yet; a cancelled or
 * not-carried-out one took nothing.
 */
export const getJobFactVolume = (job: ConnectProto.Job): number | undefined => {
  if (!isFinalJobStatus(job.status)) return undefined;
  if (tookNothing(job)) return 0;
  return job.containerCoefficients.length > 0 ? job.factTotalVolume : getJobVolume(job);
};

/**
 * Containers actually serviced at a job: the count reported for it (num_of_container),
 * else the planned one — the same count the fact volume falls back to. An unfinished
 * job has no fact yet; a cancelled or not-carried-out one took none.
 */
export const getJobFactContainerCount = (job: ConnectProto.Job): number | undefined => {
  if (!isFinalJobStatus(job.status)) return undefined;
  if (tookNothing(job)) return 0;
  return job.numOfContainer || plannedContainerCount(job);
};

/** What a job costs: its own tariff, else its pickup location's; undefined when neither is set. */
export const getJobTariff = (job: ConnectProto.Job): number | undefined =>
  Number(job.jobTariff ?? job.pickupLocation?.tariff) || undefined;
